import { useEffect, useMemo, useState } from 'react'

import { Button, Divider } from 'antd'
import { isFunction, isObject, snakeCase, values } from 'lodash'
import { useSelector } from 'react-redux'

import DataTable from '../../antd/Tables/DataTable/DataTable'
import FormFooter from '../../antd/Forms/FormFooter'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { DeleteAction, EditAction } from '../../antd/Tables/DataTable/utils/actions'
import { getComment, getFullAbbreviation, SchoolSection } from '../../../utils/entities/schoolSection'
import { isValid } from '../../../utils/validators'
import { onError } from '../../../utils/apiHelper'
import { getRandomInt } from '../../../utils'
import SchoolSectionForm from './SchoolSectionForm'
import { selectUser } from '../../../reducers/UserReducer'
import { isValidUser } from '../../../utils/roles'

export const Modes = Object.freeze({
  SELECTOR: 'selector',
  FORM: 'form'
})

const BulkSchoolSectionForm = ({ data, disabled, mode = Modes.FORM, sections, sectionToYears, useFooter, validators, onChange, onDelete, onSubmit, onCancel }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [formData, setFormData] = useState(new SchoolSection(isObject(data) ? data : {}))
  const [schoolSections, setSchoolSections] = useState([])
  const [error, setError] = useState({ missingFields: [] })

  useEffect(() => {
    if (isFunction(onChange)) {
      onChange(schoolSections)
    }
  }, [onChange, schoolSections])

  const offset = useMemo(() => (useFooter ? '268px' : '200px'), [useFooter])

  const handleSectionSubmit = event => {
    const errors = isValid(formData, values(validators))

    if (errors.keys.length > 0) {
      const errorMessages = t('common_form.error_fields', {
        fields: errors.keys.map(key => t('common.' + snakeCase(key))).join(', ')
      })

      onError({ message: errorMessages })
      setError({ missingFields: errors.keys })
    } else {
      let id = getRandomInt()

      while (schoolSections.some(q => q.id === id)) {
        id = getRandomInt()
      }

      setSchoolSections(current => [...current, new SchoolSection({ ...formData, id: -id })])
    }
  }

  const handleSubmit = () => {
    setSchoolSections([])
    onSubmit(schoolSections)
  }

  const actions = useMemo(() => {
    const deleteAction = new DeleteAction({ onDelete: schoolSection => setSchoolSections(current => current.filter(ss => ss.id !== schoolSection.id)) })
    const editAction = new EditAction({ title: t('common.edit'), onClick: setFormData }).build()

    const actions = []

    if (mode === Modes.FORM) {
      actions.push(editAction)
    }

    actions.push(deleteAction)
    return actions
  }, [t, mode, onDelete])
  const columns = useMemo(() => {
    const section = { title: t('common.section'), key: getFullAbbreviation }
    const comment = { title: t('common.comment'), key: getComment }

    const columns = [section]

    if (isValidUser(user)) {
      columns.push(comment)
    }

    return columns
  }, [user, t])

  const isDisabled = formData.section === null || formData.sectionToYear === null

  return (
    <div className='flex-column w-100 h-100'>
      <div className='flex-column w-100 pd-12'>
        <SchoolSectionForm
          data={formData}
          errorFields={error.missingFields}
          sections={sections}
          sectionToYears={sectionToYears}
          onChange={setFormData}
        />
        <div className='flex-row w-100 jc-end'>
          <Button onClick={handleSectionSubmit} disabled={isDisabled}> {t('bulk_form.buttons.add')} </Button>
        </div>
      </div>

      <Divider className='divider-4' />

      <div className='w-100 pd-12' style={{ height: `calc(100% - ${offset})` }}>
        <DataTable
          actions={actions}
          columns={columns}
          data={schoolSections}
        />
      </div>

      {useFooter && (
        <>
          <Divider className='divider-4' />

          <FormFooter
            cancel={{ text: t('modal.cancel_button'), disabled }}
            remove={null}
            submit={{ text: t('modal.ok_button.save'), disabled: schoolSections.length === 0 }}
            onSubmit={handleSubmit}
            onCancel={onCancel}
          />
        </>
      )}
    </div>
  )
}

export default BulkSchoolSectionForm
